import { computed, reactive, ref, watch } from "vue";

// 多页签导航数据（模块级单例）：
// - 首页（概览）固定存在且不可关闭
// - 一级页面按菜单路径去重；二级页面（meta.parent）按完整 path 单独成签
// - 页签存 path / 标题 / 是否固定 / 是否二级 / 路由 name / 是否缓存，
//   关闭后的跳转由组件决定
// - 页签写入 sessionStorage：刷新页面、店铺切换后仍保留已打开的页签；
//   关闭页签会同步从 keep-alive 的 include 中移除，下次打开是全新实例
const STORAGE_KEY = "lease_admin_tabs";
const HOME_TAB = {
  path: "/",
  title: "概览",
  affix: true,
  secondary: false,
  name: "Dashboard",
  keepAlive: false,
};

/** 归一化页签字段，丢弃非法项（历史缓存 / 路由改版后的脏数据） */
const normalizeTab = (tab) => {
  if (!tab || typeof tab.path !== "string" || !tab.path.startsWith("/")) {
    return null;
  }
  const isHome = tab.path === "/";
  const title =
    typeof tab.title === "string" && tab.title.trim()
      ? tab.title.trim()
      : isHome
        ? HOME_TAB.title
        : tab.path;
  return {
    path: tab.path,
    title,
    affix: isHome,
    secondary: !isHome && Boolean(tab.secondary),
    name: typeof tab.name === "string" ? tab.name : "",
    keepAlive: !isHome && Boolean(tab.keepAlive),
  };
};

const restoreTabs = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [{ ...HOME_TAB }];
    const seen = new Set([HOME_TAB.path]);
    const restored = list
      .map(normalizeTab)
      .filter((tab) => {
        if (!tab || seen.has(tab.path)) return false;
        seen.add(tab.path);
        return true;
      });
    return [{ ...HOME_TAB }, ...restored];
  } catch (e) {
    console.warn("[useTabs] 恢复页签失败，已重置为首页", e);
    return [{ ...HOME_TAB }];
  }
};

export const tabs = ref(restoreTabs());

// 页签刷新版本号：按 path 自增，参与路由视图 key，触发重新挂载
export const refreshVersions = reactive({});

// keep-alive include：只缓存当前已打开页签对应的页面，
// 关闭页签后组件名移除，Vue 会同步清理该页缓存
export const cachedViews = computed(() =>
  tabs.value
    .filter((tab) => tab.keepAlive && tab.name)
    .map((tab) => tab.name),
);

const persistTabs = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs.value));
  } catch (e) {
    console.warn("[useTabs] 保存页签失败", e);
  }
};

watch(tabs, persistTabs, { deep: true });

/**
 * 新增或更新一个页签
 * @param {{ path: string, title?: string, secondary?: boolean, name?: string, keepAlive?: boolean }} tab 页签信息
 */
export function upsertTab({
  path,
  title,
  secondary = false,
  name = "",
  keepAlive = false,
}) {
  if (!path) return;
  const found = tabs.value.find((item) => item.path === path);
  if (found) {
    // 已存在的页签保留当前标题（详情页已换成对象名时不被路由 meta.title 覆盖）
    found.secondary = secondary;
    found.keepAlive = Boolean(keepAlive);
    if (name) found.name = name;
    return;
  }
  tabs.value.push({
    path,
    title: title || path,
    affix: false,
    secondary,
    name,
    keepAlive: Boolean(keepAlive),
  });
}

/**
 * 更新指定页签的标题（详情页加载完对象名后调用）
 * @param {string} path 页签 path
 * @param {string} title 新标题
 */
export function updateTabTitle(path, title) {
  const name = String(title || "").trim();
  if (!name) return;
  const found = tabs.value.find((item) => item.path === path);
  if (found) found.title = name;
}

/**
 * 关闭一个页签（首页不可关闭）
 * @param {string} path 页签 path
 * @returns {string|null} 关闭后建议跳转的 path（关闭非当前页时返回 null）
 */
export function closeTab(path) {
  const index = tabs.value.findIndex((item) => item.path === path);
  if (index < 0 || tabs.value[index].affix) return null;
  tabs.value.splice(index, 1);
  delete refreshVersions[path];
  const fallback = tabs.value[index] || tabs.value[index - 1] || tabs.value[0];
  return fallback ? fallback.path : "/";
}

/**
 * 关闭除指定页签与固定页签外的全部页签
 * @param {string} path 保留的页签 path
 * @returns {string|null} 关闭后建议跳转的 path
 */
export function closeOtherTabs(path) {
  const target = tabs.value.find((item) => item.path === path);
  if (!target) return null;
  tabs.value = tabs.value.filter((item) => item.affix || item.path === path);
  return target.path;
}

/**
 * 关闭全部可关闭页签，仅保留固定页签（首页）
 * @returns {string} 关闭后建议跳转的 path
 */
export function closeAllTabs() {
  tabs.value = tabs.value.filter((item) => item.affix);
  return tabs.value[0]?.path || HOME_TAB.path;
}

/**
 * 清空页签（退出登录时调用，避免下一位登录用户看到上一位的页签）
 */
export function resetTabs() {
  tabs.value = [{ ...HOME_TAB }];
  Object.keys(refreshVersions).forEach((key) => delete refreshVersions[key]);
}

/**
 * 刷新指定页签：版本号自增后路由视图 key 变化，强制重新挂载
 * @param {string} path 页签 path
 */
export function refreshTab(path) {
  if (!path) return;
  refreshVersions[path] = (refreshVersions[path] || 0) + 1;
}

/**
 * 读取页签刷新版本号（参与路由视图 key）
 * @param {string} path 页签 path
 * @returns {number} 当前版本号
 */
export function tabRefreshVersion(path) {
  return refreshVersions[path] || 0;
}
